import React, { Suspense, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Route, Switch, useHistory } from 'react-router-dom';

import { LoadingComponent } from 'app/components';
import ArticleModalHandler from 'app/components/CommonStyled/ArticleModalHandler';
import { useResourceModalNavigation } from 'app/components/CommonStyled/ResourceModalNavigationHandler';
import NavBar from 'app/components/NavBar';
import Onboarding from 'app/components/Onboarding';
import AboutKyuOsOnboarding from 'app/components/Onboarding/AboutKyuOs';
import ResourceSubmissionHandler from 'app/components/ResourceSubmissionHandler';
import ResourceVotingHandler from 'app/components/ResourceVotingHandler';
import { ResourcesSection } from 'app/components/Resources';
import useCatchExpiredToken from 'hooks/useCatchExpiredToken';
import useIsLoggedIn from 'hooks/useIsLoggedIn';
import useScrollToTop from 'hooks/useScrollToTop';
import useUserData from 'hooks/useUserData';
import { clearToken } from 'services/storage';
import {
  lastHistoryEntrySelector,
  pushHistoryEntry,
  setHistory,
} from 'store/reducers/history/history-reducer';
import { isDrawerOpenSelector, setIsDrawerOpen } from 'store/reducers/navbar-reducer';
import {
  aboutKyuOsHiddenSelector,
  isOnboardingOpenSelector,
  setAboutKyuOsHidden,
  setAboutKyuOsOpen,
} from 'store/reducers/onboarding-reducer';
import { expirationTimeSelector, profileCompletionSelector } from 'store/reducers/user-reducer';
import { mapModels, newDetailsPages } from 'utils/constants';

import PrivateRoute from './PrivateRoute';

const HashDrawersHandler = React.lazy(() => import('app/components/HashDrawersHandler'));
const BestOfScreenGallery = React.lazy(() => import('app/features/BestOf/design/BestOfScreenGallery'));
const BestOfBannerGallery = React.lazy(() => import('app/features/BestOf/BestOfBannerGallery'));
const TheWellReportPage = React.lazy(() => import('app/pages/TheWellReport'));
const PulseTeamingReportPage = React.lazy(() => import('app/pages/PulseTeamingReport'));
const PulseBd100ReportPage = React.lazy(() => import('app/pages/PulseBd100Report'));
const Map = React.lazy(() => import('app/components/Map'));
const AbstractMap = React.lazy(() => import('app/components/Map/Abstract/AbstractMap'));
const AboutKyuOsPage = React.lazy(() => import('app/pages/AboutKyuOsPage'));
const Articles = React.lazy(() => import('app/pages/Articles'));
const ArticlesIndexPage = React.lazy(() => import('app/pages/ArticlesIndex'));
const AuthPage = React.lazy(() => import('app/pages/AuthPage'));
const BlogPostPage = React.lazy(() => import('app/pages/BlogPostPage'));
const CollectionDetailsPage = React.lazy(() => import('app/pages/CollectionDetailsPage'));
const CollectivePage = React.lazy(() => import('app/pages/CollectivePage'));
const CommunitiesPage = React.lazy(() => import('app/pages/CommunitiesPage'));
const CompanyResourcePage = React.lazy(() => import('app/pages/CompanyResource'));
const FinancialReportPage = React.lazy(() => import('app/pages/FinancialReport'));
const Home = React.lazy(() => import('app/pages/Home'));
const InfoPage = React.lazy(() => import('app/pages/InfoPage'));
const ConveningPage = React.lazy(() => import('app/pages/ConveningPage'));
const CommunityPage = React.lazy(() => import('app/pages/CommunityPage'));
const MyProfile = React.lazy(() => import('app/pages/MyProfile'));
const NewDetailsPage = React.lazy(() => import('app/pages/NewDetailsPage'));
const NotFoundPage = React.lazy(() => import('app/pages/NotFoundPage'));
const ProfileEditor = React.lazy(() => import('app/pages/ProfileEditor'));
const ReportsPage = React.lazy(() => import('app/pages/Reports'));
const ResourcesPage = React.lazy(() => import('app/pages/Resources'));
const SearchPage = React.lazy(() => import('app/pages/Search'));
const TheWellIndex = React.lazy(() => import('app/pages/TheWell'));
const Tools = React.lazy(() => import('app/pages/Tools'));
const ToolsIndexPage = React.lazy(() => import('app/pages/Tools/ToolsIndexPage'));
const PeoplePage = React.lazy(() => import('app/pages/PeoplePage'));
const TeamingPage = React.lazy(() => import('app/pages/TeamingPage'));
const KyuPulsePage = React.lazy(() => import('app/pages/KyuPulsePage'));
const ResourcesSubmissionPage = React.lazy(() => import('app/pages/ResourcesSubmissionPage'));
const ResourceVotingPage = React.lazy(() => import('app/pages/ResourceVotingPage'));
const ResourceVotingWinnersPage = React.lazy(() => import('app/pages/ResourceVotingWinnersPage'));
const BestOf2025Page = React.lazy(() => import('app/pages/BestOf2025Page'));
const BestOfPage = React.lazy(() => import('app/pages/BestOfPage'));
const BestOfResultsPage = React.lazy(() => import('app/pages/BestOfResultsPage'));
const BestOfJuryReviewPage = React.lazy(() => import('app/pages/BestOfJuryReviewPage'));
const BD100Page = React.lazy(() => import('app/pages/BD100Page'));

export default function Routes() {
  useScrollToTop();
  useCatchExpiredToken();
  const userData = useUserData();
  const expirationTime = useSelector(expirationTimeSelector) as number;
  const { isLoggedIn } = useIsLoggedIn();
  useResourceModalNavigation();
  const { push, go, location } = useHistory();
  const dispatch = useDispatch();
  const lastHistoryEntry = useSelector(lastHistoryEntrySelector);
  const isDrawerOpen = useSelector(isDrawerOpenSelector);
  const [showAboutKyuOsOnboarding, setShowAboutKyuOsOnboarding] = useState<boolean>(false);
  const aboutKyuOsOnboardingHidden = useSelector(aboutKyuOsHiddenSelector);
  const profileCompletion: number = useSelector(profileCompletionSelector);
  const isOnboardingOpen = useSelector(isOnboardingOpenSelector);

  const handleSessionTimeout = () => {
    clearToken();
    push({ pathname: location.pathname, state: { logged_out: true } });
    go(0);
  };

  useEffect(() => {
    let timeoutId = null;
    if (expirationTime && isLoggedIn) {
      timeoutId = setTimeout(handleSessionTimeout, expirationTime * 1000);
    }

    return () => {
      clearTimeout(timeoutId);
    };
  }, [expirationTime, isLoggedIn]);

  const dispatchDrawerOpen = (state: boolean) => {
    dispatch(setIsDrawerOpen(state));
  };

  useEffect(() => {
    if (isDrawerOpen) {
      dispatchDrawerOpen(false);
    }
  }, [location]);

  useEffect(() => {
    if (!lastHistoryEntry) {
      dispatch(setHistory([location]));
    }

    if (lastHistoryEntry && lastHistoryEntry.pathname !== location.pathname) {
      dispatch(pushHistoryEntry(location));
    }
  }, [location, lastHistoryEntry]);

  const showOnboardedProfile = useMemo(() => {
    return !userData.onboarding_profile && profileCompletion >= 50;
  }, [profileCompletion, userData]);

  useEffect(() => {
    let timeoutId = null;

    if (
      Object.keys(userData).length > 0 &&
      !userData.onboarded &&
      location.pathname !== '/kyu-101' &&
      !userData.featured_banner
    ) {
      timeoutId = setTimeout(() => {
        dispatch(setAboutKyuOsHidden(false));
        setShowAboutKyuOsOnboarding(true);
        if (!userData.onboarding_started) {
          dispatch(setAboutKyuOsOpen(true));
        }
      }, 2000);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [userData]);

  return (
    <Suspense fallback={<LoadingComponent idParticles="graph-page-particles" messages={[]} />}>
      {isLoggedIn ? <NavBar /> : null}
      {isLoggedIn ? <ResourceSubmissionHandler /> : null}
      {isLoggedIn ? <ResourceVotingHandler /> : null}
      {isLoggedIn ? <HashDrawersHandler /> : null}
      {isLoggedIn ? <ArticleModalHandler /> : null}
      {isLoggedIn && false ? <AboutKyuOsOnboarding /> : null}
      {isLoggedIn && showOnboardedProfile && isOnboardingOpen ? <Onboarding /> : null}
      <Switch>
        <Route exact path="/">
          <Home />
        </Route>
        <PrivateRoute exact path="/my-profile">
          <MyProfile />
        </PrivateRoute>
        <PrivateRoute exact path="/my-profile/edit">
          <ProfileEditor />
        </PrivateRoute>
        <Route exact path="/auth">
          <AuthPage />
        </Route>
        <PrivateRoute exact path="/search">
          <SearchPage />
        </PrivateRoute>
        <PrivateRoute exact path="/info-page">
          <InfoPage />
        </PrivateRoute>
        <PrivateRoute path={`/:model(${mapModels.join('|')})/:slug/map/:type?`}>
          <Map />
        </PrivateRoute>
        <PrivateRoute exact path={`/176650c51304da92a201117482492d66-vote`}>
          <ResourceVotingPage />
        </PrivateRoute>
        <PrivateRoute exact path={`/176650c51304da92a201117482492d66-entry`}>
          <ResourcesSubmissionPage />
        </PrivateRoute>
        <PrivateRoute exact path={`/176650c51304da92a201117482492d66-winners`}>
          <ResourceVotingWinnersPage />
        </PrivateRoute>
        <PrivateRoute exact path="/design/best-of-screens">
          <BestOfScreenGallery />
        </PrivateRoute>
        <PrivateRoute exact path="/design/best-of-banners">
          <BestOfBannerGallery />
        </PrivateRoute>
        <PrivateRoute exact path="/best-of/:year/results">
          <BestOfResultsPage />
        </PrivateRoute>
        <PrivateRoute exact path="/best-of/:year/jury-review">
          <BestOfJuryReviewPage />
        </PrivateRoute>
        <PrivateRoute exact path="/best-of/:year">
          <BestOfPage />
        </PrivateRoute>
        <PrivateRoute exact path={`/best-of-2025`}>
          <BestOf2025Page />
        </PrivateRoute>
        <PrivateRoute exact path={`/resources/my-resources`}>
          <ResourcesPage section={ResourcesSection.MY_RESOURCES} />
        </PrivateRoute>
        <PrivateRoute
          exact
          path={[
            '/resources/best-of/:year/:libraryView(winners|all|my-company|jury-prizes)',
            '/resources/best-of/:year/category/:categorySlug',
          ]}
        >
          <ResourcesPage section={ResourcesSection.BEST_OF_LIBRARY} />
        </PrivateRoute>
        <PrivateRoute exact path={`/resources/best-of-2025-winners`}>
          <ResourcesPage section={ResourcesSection.BEST_OF_2025_WINNERS} />
        </PrivateRoute>
        <PrivateRoute exact path={`/resources/best-of-2025-category/:category`}>
          <ResourcesPage section={ResourcesSection.BEST_OF_2025_CATEGORY} />
        </PrivateRoute>
        <PrivateRoute exact path={`/resources/best-of-2025-my-company`}>
          <ResourcesPage section={ResourcesSection.BEST_OF_2025_MY_COMPANY} />
        </PrivateRoute>
        <PrivateRoute exact path={`/resources/:slug`}>
          <ResourcesPage showDetailPage />
        </PrivateRoute>
        <PrivateRoute exact path={`/resources/:slug/embed`}>
          <CompanyResourcePage />
        </PrivateRoute>
        <PrivateRoute
          exact
          path={[
            `/dashboards/:reportType`,
            `/dashboards/:reportType/:companySlug`,
          ]}
        >
          <FinancialReportPage />
        </PrivateRoute>
        <PrivateRoute path={`/resources`}>
          <ResourcesPage />
        </PrivateRoute>
        <PrivateRoute path={`/people`}>
          <PeoplePage />
        </PrivateRoute>
        <PrivateRoute exact path="/teaming">
          <TeamingPage />
        </PrivateRoute>
        <PrivateRoute path={`/partner-company/:slug`}>
          <NewDetailsPage attributes={{ model: 'member-company' }} />
        </PrivateRoute>
        <PrivateRoute path={`/:model(${newDetailsPages.join('|')})/:slug`}>
          <NewDetailsPage />
        </PrivateRoute>
        <PrivateRoute path="/convening/:slug">
          <ConveningPage onlyConvening />
        </PrivateRoute>
        <PrivateRoute exact path="/communities">
          <CommunitiesPage />
        </PrivateRoute>
        <PrivateRoute exact path="/community/health-and-well-being/firing-up-project-spark">
          <BlogPostPage />
        </PrivateRoute>
        <PrivateRoute path="/community/:communitySlug/:slug">
          <ConveningPage fromCommunity />
        </PrivateRoute>
        <PrivateRoute path={['/community/:slug', '/hub/:slug']}>
          <CommunityPage />
        </PrivateRoute>
        <PrivateRoute exact path="/pulse/:section(clients|resources)?">
          <KyuPulsePage />
        </PrivateRoute>
        <PrivateRoute exact path="/kyu">
          <NewDetailsPage attributes={{ model: 'member-company', slug: 'kyu' }} />
        </PrivateRoute>
        <PrivateRoute exact path="/kyu-101">
          <AboutKyuOsPage />
        </PrivateRoute>
        <PrivateRoute exact path="/reports">
          <ReportsPage />
        </PrivateRoute>
        <PrivateRoute exact path="/teaming-report">
          <PulseTeamingReportPage />
        </PrivateRoute>
        <PrivateRoute exact path="/bd100-report">
          <PulseBd100ReportPage />
        </PrivateRoute>
        <PrivateRoute exact path="/the-well-report">
          <TheWellReportPage />
        </PrivateRoute>
        <Route exact path="/collective">
          <CollectivePage />
        </Route>
        <PrivateRoute exact path="/the-well">
          <TheWellIndex />
        </PrivateRoute>
        <PrivateRoute exact path="/collection/:slug">
          <CollectionDetailsPage />
        </PrivateRoute>
        <PrivateRoute exact path="/tools">
          <ToolsIndexPage />
        </PrivateRoute>
        <PrivateRoute exact path="/tools/:slug">
          <Tools />
        </PrivateRoute>
        <PrivateRoute exact path="/articles">
          <ArticlesIndexPage />
        </PrivateRoute>
        <PrivateRoute exact path="/articles/:slug">
          <Articles />
        </PrivateRoute>
        <PrivateRoute exact path="/network-map/:slug">
          <AbstractMap slug={location.pathname.split('/')[2]} />
        </PrivateRoute>
        <PrivateRoute exact path="/bd-100">
          <BD100Page />
        </PrivateRoute>
        <Route>
          <NotFoundPage />
        </Route>
      </Switch>
    </Suspense>
  );
}
